<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-bold text-gray-900">Calendar View</h2>
      <div class="flex items-center space-x-4">
        <button
          @click="previousMonth"
          class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h3 class="text-lg font-semibold min-w-[150px] text-center">
          {{ format(currentMonth, 'MMMM yyyy') }}
        </h3>
        <button
          @click="nextMonth"
          class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      <p class="mt-4 text-gray-600">Loading bookings...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-red-800">{{ error }}</p>
    </div>

    <!-- Calendar -->
    <div v-else class="space-y-4">
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="bg-blue-50 p-4 rounded-lg">
          <p class="text-sm text-blue-600 font-medium">Total Bookings</p>
          <p class="text-2xl font-bold text-blue-900">{{ stats.totalBookings || 0 }}</p>
        </div>
        <div class="bg-green-50 p-4 rounded-lg">
          <p class="text-sm text-green-600 font-medium">Paid</p>
          <p class="text-2xl font-bold text-green-900">{{ stats.paidBookings || 0 }}</p>
        </div>
        <div class="bg-yellow-50 p-4 rounded-lg">
          <p class="text-sm text-yellow-600 font-medium">Unpaid</p>
          <p class="text-2xl font-bold text-yellow-900">{{ stats.unpaidBookings || 0 }}</p>
        </div>
        <div class="bg-purple-50 p-4 rounded-lg">
          <p class="text-sm text-purple-600 font-medium">Total Guests</p>
          <p class="text-2xl font-bold text-purple-900">{{ stats.totalGuests || 0 }}</p>
        </div>
      </div>

      <!-- Calendar Grid -->
      <div class="border rounded-lg overflow-hidden">
        <!-- Day Headers -->
        <div class="grid grid-cols-7 bg-gray-50 border-b">
          <div v-for="day in ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']" :key="day" class="p-3 text-center text-sm font-semibold text-gray-700">
            {{ day }}
          </div>
        </div>

        <!-- Calendar Days -->
        <div class="grid grid-cols-7">
          <div
            v-for="day in calendarDays"
            :key="day.dateStr"
            :class="[
              'min-h-[120px] p-2 border-b border-r',
              !day.isCurrentMonth ? 'bg-gray-50' : 'bg-white',
              day.isToday ? 'bg-blue-50' : ''
            ]"
          >
            <div class="flex justify-between items-start mb-2">
              <span
                :class="[
                  'text-sm font-medium',
                  !day.isCurrentMonth ? 'text-gray-400' : 'text-gray-700',
                  day.isToday ? 'text-blue-600 font-bold' : ''
                ]"
              >
                {{ day.day }}
              </span>
              <div class="flex items-center gap-1">
                <span v-if="day.hasHoliday" class="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded" title="Holiday">
                  🏖️
                </span>
                <span v-if="day.bookingCount > 0" class="text-xs bg-black text-white px-2 py-1 rounded-full">
                  {{ day.bookingCount }}
                </span>
              </div>
            </div>

            <!-- Holidays for this day -->
            <div v-if="day.hasHoliday" class="mb-1">
              <div
                v-for="holiday in day.holidays"
                :key="holiday.name + holiday.date"
                :class="[
                  'text-xs p-1 rounded mb-1',
                  holiday.type === 'google' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-orange-50 text-orange-700 border border-orange-200'
                ]"
              >
                <p class="font-medium truncate">🏖️ {{ holiday.name }}</p>
                <p v-if="holiday.type === 'custom'" class="text-xs opacity-75">Custom Holiday</p>
                <p v-if="holiday.cruiseShips && holiday.cruiseShips.length" class="text-xs opacity-75 truncate">
                  {{ holiday.cruiseShips.map(ship => ship.ship).join(', ') }}
                  <span v-if="holiday.totalEstimatedPassengers"> - {{ holiday.totalEstimatedPassengers.toLocaleString() }} pax</span>
                </p>
              </div>
            </div>

            <!-- Bookings for this day -->
            <div class="space-y-1">
              <div
                v-for="booking in day.bookings.slice(0, 3)"
                :key="booking.reference"
                @click="selectBooking(booking)"
                :class="[
                  'text-xs p-1 rounded cursor-pointer hover:opacity-80 transition-opacity',
                  booking.status === 'paid' ? 'bg-green-100 text-green-800' :
                  booking.status === 'unpaid' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                ]"
              >
                <p class="font-medium truncate">{{ booking.time }} - {{ booking.firstName }} {{ booking.lastName }}</p>
                <p class="truncate">{{ booking.guestCount }} guests</p>
              </div>
              <p v-if="day.bookings.length > 3" class="text-xs text-gray-500 pl-1">
                +{{ day.bookings.length - 3 }} more
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Booking Detail Modal -->
    <div
      v-if="selectedBooking"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="selectedBooking = null"
    >
      <div class="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-start mb-4">
          <h3 class="text-xl font-bold">Booking Details</h3>
          <button @click="selectedBooking = null" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="space-y-3">
          <div>
            <p class="text-sm text-gray-500">Reference</p>
            <p class="font-semibold">{{ selectedBooking.reference }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Guest Name</p>
            <p class="font-semibold">{{ selectedBooking.firstName }} {{ selectedBooking.lastName }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Email</p>
            <p class="font-semibold">{{ selectedBooking.email }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Date & Time</p>
            <p class="font-semibold">{{ selectedBooking.date }} at {{ selectedBooking.time }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Tour Type</p>
            <p class="font-semibold">{{ selectedBooking.tourType }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Number of Guests</p>
            <p class="font-semibold">{{ selectedBooking.guestCount }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Language</p>
            <p class="font-semibold">{{ selectedBooking.preferredLanguage }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Total Price</p>
            <p class="font-semibold">{{ selectedBooking.totalPrice }} VT</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Status</p>
            <span
              :class="[
                'inline-block px-3 py-1 rounded-full text-sm font-medium',
                selectedBooking.status === 'paid' ? 'bg-green-100 text-green-800' :
                selectedBooking.status === 'unpaid' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              ]"
            >
              {{ selectedBooking.status }}
            </span>
          </div>
        </div>

        <div class="mt-6 pt-6 border-t flex justify-end">
          <button
            @click="selectedBooking = null"
            class="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  getDay,
  isToday,
  isSameMonth,
  startOfWeek,
  endOfWeek
} from 'date-fns'
import { useAuth } from '../../composables/useAuth'

const { getAuthAxios } = useAuth()
const currentMonth = ref(new Date())
const bookings = ref([])
const holidays = ref([])
const customHolidays = ref([])
const stats = ref({})
const loading = ref(false)
const error = ref(null)
const selectedBooking = ref(null)

const fetchBookings = async () => {
  loading.value = true
  error.value = null

  try {
    const dateFrom = format(startOfMonth(currentMonth.value), 'yyyy-MM-dd')
    const dateTo = format(endOfMonth(currentMonth.value), 'yyyy-MM-dd')
    const timeMin = startOfMonth(currentMonth.value).toISOString()
    const timeMax = endOfMonth(currentMonth.value).toISOString()

    const authAxios = getAuthAxios()

    // Fetch bookings, Google holidays, and custom holidays in parallel
    const [bookingsRes, googleHolidaysRes, customHolidaysRes] = await Promise.all([
      authAxios.get('/api/admin-bookings', {
        params: { dateFrom, dateTo, limit: 1000 }
      }),
      authAxios.get('/api/holidays', {
        params: { timeMin, timeMax }
      }),
      authAxios.get('/api/admin-holidays')
    ])

    if (bookingsRes.data.success) {
      bookings.value = bookingsRes.data.bookings
      stats.value = bookingsRes.data.stats
    }

    // Process Google Calendar holidays
    if (googleHolidaysRes.data?.items) {
      holidays.value = googleHolidaysRes.data.items.map(h => ({
        name: h.summary,
        date: h.start.date,
        type: 'google'
      }))
    }

    // Process custom holidays
    if (customHolidaysRes.data.success) {
      customHolidays.value = customHolidaysRes.data.holidays.map(h => ({
        ...h,
        type: 'custom'
      }))
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load data'
    console.error('Error fetching data:', err)
  } finally {
    loading.value = false
  }
}

const calendarDays = computed(() => {
  const start = startOfMonth(currentMonth.value)
  const end = endOfMonth(currentMonth.value)

  // Get the first day of the week for the month
  let firstDay = getDay(start)
  // Adjust so Monday is 0, Sunday is 6
  firstDay = firstDay === 0 ? 6 : firstDay - 1

  // Calculate calendar start (include previous month days)
  const calendarStart = new Date(start)
  calendarStart.setDate(calendarStart.getDate() - firstDay)

  // Calculate calendar end (6 weeks max)
  const calendarEnd = new Date(calendarStart)
  calendarEnd.setDate(calendarEnd.getDate() + 41) // 6 weeks

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd })

  return days.map(date => {
    const dateStr = format(date, 'yyyy-MM-dd')
    const dayBookings = bookings.value.filter(b => b.date === dateStr)
    const dayHolidays = [
      ...holidays.value.filter(h => h.date === dateStr),
      ...customHolidays.value.filter(h => h.date === dateStr)
    ]

    return {
      date,
      dateStr,
      day: format(date, 'd'),
      isCurrentMonth: isSameMonth(date, currentMonth.value),
      isToday: isToday(date),
      bookings: dayBookings,
      bookingCount: dayBookings.length,
      holidays: dayHolidays,
      hasHoliday: dayHolidays.length > 0
    }
  })
})

const previousMonth = () => {
  currentMonth.value = subMonths(currentMonth.value, 1)
}

const nextMonth = () => {
  currentMonth.value = addMonths(currentMonth.value, 1)
}

const selectBooking = (booking) => {
  selectedBooking.value = booking
}

watch(currentMonth, () => {
  fetchBookings()
})

onMounted(() => {
  fetchBookings()
})
</script>
